import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { SITE_ID } from '@/lib/constants';
import type { Product } from '../../../hooks/useProducts';
import ProductQA from './ProductQA';

interface Review {
  id: string;
  musteri_adi: string;
  puan: number;
  yorum_baslik: string | null;
  yorum_metni: string;
  olusturma_tarihi: string;
}

interface ProductTabsProps {
  product: Product;
}

type Tab = 'description' | 'features' | 'reviews' | 'qa';

export default function ProductTabs({ product }: ProductTabsProps) {
  const [activeTab, setActiveTab] = useState<Tab>('description');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [qaCount, setQaCount] = useState<number | null>(null);


  const tabs: { key: Tab; label: string; icon: string; count?: number | null }[] = [
    { key: 'description', label: 'Açıklama', icon: 'ri-file-text-line' },
    { key: 'features', label: 'Özellikler', icon: 'ri-list-check-2' },
    { key: 'reviews', label: 'Yorumlar', icon: 'ri-star-line', count: reviews.length > 0 ? reviews.length : null },
    { key: 'qa', label: 'Sorular & Cevaplar', icon: 'ri-question-answer-line', count: qaCount },
  ];

  // Soru sayısını sayfa açılışında çek
  useEffect(() => {
    let cancelled = false;
    async function fetchQaCount() {
      const targetIds = [product.id];
      if (product.ana_urun_id) targetIds.push(product.ana_urun_id);

      const { count } = await supabase
        .from('urun_sorulari')
        .select('id', { count: 'exact', head: true })
        .eq('site_id', SITE_ID)
        .in('urun_id', targetIds)
        .eq('cevaplandi', true);

      if (!cancelled) {
        setQaCount(count ?? 0);
      }
    }
    fetchQaCount();
    return () => { cancelled = true; };
  }, [product.id, product.ana_urun_id]);

  // Onaylı yorumları çek — varyasyon ise ana ürünün yorumlarını da dahil et
  useEffect(() => {
    let cancelled = false;
    async function fetchReviews() {
      setLoadingReviews(true);

      // Hangi ürün ID'leri sorgulanacak: mevcut ürün + varsa ana ürün
      const targetIds = [product.id];
      if (product.ana_urun_id) targetIds.push(product.ana_urun_id);

      const { data } = await supabase
        .from('urun_yorumlari')
        .select('id, musteri_adi, puan, yorum_baslik, yorum_metni, olusturma_tarihi')
        .eq('site_id', SITE_ID)
        .in('urun_id', targetIds)
        .eq('onaylandi', true)
        .order('olusturma_tarihi', { ascending: false });

      if (!cancelled) {
        setReviews(data || []);
        setLoadingReviews(false);
      }
    }
    fetchReviews();
    return () => { cancelled = true; };
  }, [product.id, product.ana_urun_id]);

  // Ortalama puanı hesapla
  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + (r.puan || 0), 0) / reviews.length 
    : product.rating || 0;

  // Puan dağılımını hesapla
  const ratingCounts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.puan === star).length,
  }));
  const maxCount = Math.max(...ratingCounts.map(r => r.count), 1);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const renderStars = (count: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
        <i className={`text-xs ${i < count ? 'ri-star-fill text-gold' : 'ri-star-line text-cream-dark'}`} />
      </div>
    ));

  const hasFeatures = product.weight || product.category_name || (product.ozellikler && Object.keys(product.ozellikler).length > 0);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-cream-dark">
      {/* Tab Header */}
      <div className="flex border-b border-cream-dark bg-cream/40">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer relative ${
              activeTab === tab.key
                ? 'text-coffee'
                : 'text-coffee-light hover:text-coffee-medium'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${tab.icon} text-sm`} />
            </div>
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span className="ml-1 text-xs font-normal opacity-70">({tab.count})</span>
            )}
            {activeTab === tab.key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-8">
        {activeTab === 'description' && (
          <div className="max-w-3xl">
            {product.aciklama || product.short_description ? (
              <div className="prose prose-sm max-w-none">
                <p className="text-[15px] text-coffee-light leading-loose">
                  {product.aciklama || product.short_description}
                </p>

              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-cream-dark">
                  <i className="ri-file-text-line text-3xl" />
                </div>
                <p className="text-sm text-coffee-light">Açıklama bulunmuyor.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'features' && (
          <div>
            {hasFeatures ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y divide-cream-dark md:divide-y-0">
                {[
                  product.weight && { label: 'Ağırlık', value: product.weight, icon: 'ri-scales-2-line' },
                  product.category_name && { label: 'Kategori', value: product.category_name, icon: 'ri-price-tag-3-line' },
                  ...(product.ozellikler
                    ? Object.entries(product.ozellikler).map(([k, v]) => ({ label: k, value: v, icon: 'ri-checkbox-circle-line' }))
                    : []),
                ].filter(Boolean).map((feat, i) => (
                  <div key={i} className={`flex items-center gap-4 py-4 ${i % 2 === 0 ? 'md:pr-8 md:border-r md:border-cream-dark' : 'md:pl-8'} ${i < (Object.keys(product.ozellikler || {}).length + 2) - 2 ? 'border-b border-cream-dark' : ''}`}>
                    <div className="w-8 h-8 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0">
                      <i className={`${feat!.icon} text-gold text-sm`} />
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm font-medium text-coffee">{feat!.label}</span>
                      <span className="text-sm text-coffee-light">{feat!.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-cream-dark">
                  <i className="ri-list-check-2 text-3xl" />
                </div>
                <p className="text-sm text-coffee-light">Özellik bilgisi bulunmuyor.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            {/* Overall Rating */}
            <div className="flex items-start gap-8 mb-8 pb-8 border-b border-cream-dark">
              <div className="text-center flex-shrink-0">
                <p className="font-serif text-5xl font-bold text-coffee">{averageRating.toFixed(1)}</p>
                <div className="flex items-center gap-0.5 justify-center mt-1 mb-1">
                  {renderStars(Math.round(averageRating))}
                </div>
                <p className="text-xs text-coffee-light">{reviews.length} yorum</p>
              </div>
              <div className="flex-1 space-y-1.5">
                {ratingCounts.map(({ star, count }) => {
                  const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <span className="text-xs text-coffee-light w-2">{star}</span>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-gold text-xs" />
                      </div>
                      <div className="flex-1 h-1.5 bg-cream rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gold rounded-full transition-all duration-300" 
                          style={{ width: `${maxCount > 0 ? (count / maxCount) * 100 : 0}%` }} 
                        />
                      </div>
                      <span className="text-xs text-coffee-light w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Reviews List */}
            {loadingReviews ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-4 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-cream flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-cream rounded w-1/4" />
                      <div className="h-3 bg-cream rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-cream-dark">
                  <i className="ri-star-line text-3xl" />
                </div>
                <p className="text-sm text-coffee-light">Henüz yorum yok.</p>
                <p className="text-xs text-coffee-light/70 mt-1">İlk yorumu siz yapın!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((r) => (
                  <div key={r.id} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-coffee flex items-center justify-center flex-shrink-0 text-cream font-semibold text-sm">
                      {r.musteri_adi.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-coffee">{r.musteri_adi}</span>
                        <span className="text-xs text-coffee-light">{formatDate(r.olusturma_tarihi)}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {renderStars(r.puan)}
                      </div>
                      {r.yorum_baslik && (
                        <p className="text-sm font-semibold text-coffee mb-1">{r.yorum_baslik}</p>
                      )}
                      <p className="text-sm text-coffee-light leading-relaxed">{r.yorum_metni}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'qa' && (
          <ProductQA
            productId={product.id}
            anaUrunId={product.ana_urun_id ?? undefined}
            onCountChange={setQaCount}
          />
        )}
      </div>
    </div>
  );
}
